import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, ListGroup, ListGroupItem, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import { Random } from 'meteor/random';
import Validation from '../../containers/Validation';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

const UserProfileNameForm = ({ user }) => {
  const userProfileName = user && user.profile && user.profile.name;
  if (!userProfileName) return '';
  return (
    <Form.Row>
      <Form.Group as={Col} xs={6}>
        <Form.Label>First Name</Form.Label>
        <Form.Control
          disabled={user && user.service !== 'password'}
          type="text"
          name="firstName"
          defaultValue={user && user.profile && user.profile.name.first}
        />
      </Form.Group>
      <Form.Group as={Col} xs={6}>
        <Form.Label>Last Name</Form.Label>
        <Form.Control
          disabled={user && user.service !== 'password'}
          type="text"
          name="lastName"
          defaultValue={user && user.profile && user.profile.name.last}
        />
      </Form.Group>
    </Form.Row>
  );
};
UserProfileNameForm.propTypes = {
  user: PropTypes.object.isRequired
};

const UserNameForm = ({ user }) => {
  const userName = user && user.username;
  if (!userName) return '';
  return (
    <Form.Row>
      <Form.Group as={Col} xs={12}>
        <Form.Label>Username</Form.Label>
        <Form.Control
          disabled={user && user.service !== 'password'}
          type="text"
          name="username"
          defaultValue={user && user.username}
        />
      </Form.Group>
    </Form.Row>
  );
};
UserNameForm.propTypes = {
  user: PropTypes.object.isRequired
};

const ChangePasswordForm = ({
  user,
  isPasswordVisible,
  password,
  toggleShowPassword,
  updatePassword,
  generatePassword
}) => {
  const passwordUser = user && user.service === 'password';
  if (!passwordUser) return '';
  return (
    <Row>
      <Form.Group as={Col} xs={12}>
        <Form.Row className="justify-content-between no-gutters">
          <Form.Label column>Password</Form.Label>
          <Col>
            <Form.Check
              inline
              checked={isPasswordVisible}
              className="float-right"
              onChange={toggleShowPassword}
              label="Show Password"
            />
          </Col>
        </Form.Row>
        <InputGroup>
          <Form.Control
            type={isPasswordVisible ? 'text' : 'password'}
            name="password"
            autoComplete="off"
            value={password}
            onChange={updatePassword}
          />
          <InputGroup.Prepend>
            <Button onClick={generatePassword}>
              <FontAwesomeIcon icon="random" /> Generate
            </Button>
          </InputGroup.Prepend>
        </InputGroup>
        <Form.Text className="text-muted">Use at least six characters.</Form.Text>
      </Form.Group>
    </Row>
  );
};
ChangePasswordForm.propTypes = {
  user: PropTypes.object.isRequired,
  isPasswordVisible: PropTypes.bool.isRequired,
  password: PropTypes.string.isRequired,
  toggleShowPassword: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  generatePassword: PropTypes.func.isRequired
};

const AdminUserProfileForm = ({
  loading,
  user,
  roles,
  isPasswordVisible,
  password,
  toggleShowPassword,
  updatePassword,
  handleDeleteUser,
  generatePassword
}) => {
  if (loading) return '';
  return (
    <>
      <UserProfileNameForm user={user} />
      <UserNameForm user={user} />
      <Form.Row>
        <Form.Group as={Col} xs={12}>
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            disabled={user && user.service !== 'password'}
            type="text"
            name="emailAddress"
            autoComplete="off"
            defaultValue={user && user.emails && user.emails[0].address}
          />
        </Form.Group>
        <Form.Group as={Col} xs={12}>
          <Form.Label>Roles</Form.Label>
          <ListGroup>
            {roles.map(({ _id, name, inRole }) => (
              <ListGroupItem key={_id}>
                <Form.Check
                  name="role"
                  value={name}
                  defaultChecked={inRole}
                  inline
                  label={capitalize(name)}
                />
              </ListGroupItem>
            ))}
          </ListGroup>
        </Form.Group>
      </Form.Row>
      <ChangePasswordForm
        user={user}
        isPasswordVisible={isPasswordVisible}
        password={password}
        toggleShowPassword={toggleShowPassword}
        updatePassword={updatePassword}
        generatePassword={generatePassword}
      />
      <Button type="submit" variant="success">
        {user ? 'Save Changes' : 'Create User'}
      </Button>
      {user && (
        <Button variant="danger" className="float-right" onClick={handleDeleteUser}>
          Delete User
        </Button>
      )}
    </>
  );
};
AdminUserProfileForm.propTypes = {
  loading: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  isPasswordVisible: PropTypes.bool.isRequired,
  password: PropTypes.string.isRequired,
  toggleShowPassword: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  generatePassword: PropTypes.func.isRequired,
  handleDeleteUser: PropTypes.func.isRequired
};

class AdminUserProfile extends React.Component {
  state = { isPasswordVisible: false, password: '' };

  handleSubmit = async form => {
    const { user } = this.props;
    const existingUser = user;
    const isPasswordUser = (existingUser && existingUser.service === 'password') || !existingUser;
    const method = existingUser ? 'edit' : 'create';
    const password = isPasswordUser ? form.password.value : null;
    const roles = [];
    form.role.forEach(role => {
      if (role.checked) {
        roles.push(role.value);
      }
    });

    let newUser;

    if (isPasswordUser) {
      newUser = {
        email: form.emailAddress.value,
        password,
        profile: {
          name: {
            first: form.firstName.value,
            last: form.lastName.value
          }
        },
        roles
      };
    }

    if (!isPasswordUser) {
      newUser = {
        roles
      };
    }

    if (existingUser) newUser._id = existingUser._id;

    try {
      const newUserId = await methodPromise(`admin.${method}User`, newUser);
      const { history } = this.props;
      if (method === 'create') history.push(`/admin/users/${newUserId}`);
      Bert.alert(method === 'edit' ? 'User updated!' : 'User created!', 'success');
      if (this.password) this.setState({ password: '' });
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleDeleteUser = async () => {
    // FIXME: eliminar confirm
    // eslint-disable-next-line no-alert,no-restricted-globals
    if (confirm("Are you sure? This will permanently delete this user's account!")) {
      const { user } = this.props;
      try {
        const { deletedFiles, deletedFolders } = await methodPromise('users.hardDelete', user._id);
        const { history } = this.props;
        Bert.alert(`User deleted! ${deletedFiles} files and ${deletedFolders} folders`, 'danger');
        history.push('/admin/users');
      } catch (error) {
        handleErrorInClient(error);
      }
    }
  };

  generatePassword = () => {
    this.setState({ password: Random.hexString(20) });
  };

  updatePassword = event => {
    this.setState({ password: event.target.value });
  };

  toggleShowPassword = () => {
    this.setState(prevState => ({
      isPasswordVisible: !prevState.isPasswordVisible
    }));
  };

  render() {
    const { loading, user, roles } = this.props;
    const { isPasswordVisible, password } = this.state;
    const validationProps = {
      rules: {
        firstName: {
          required: true
        },
        lastName: {
          required: true
        },
        emailAddress: {
          required: true,
          email: true
        },
        password: {
          minlength: 6
        }
      },
      messages: {
        firstName: {
          required: "What's the user's first name?"
        },
        lastName: {
          required: "What's the user's last name?"
        },
        emailAddress: {
          required: 'Need an email address here.',
          email: 'Is this email address correct?'
        },
        password: {
          minlength: 'Please use at least six characters.'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };
    return (
      <Row>
        <Col xs={12} md={6}>
          <Validation {...validationProps}>
            <Form onSubmit={event => event.preventDefault()}>
              <AdminUserProfileForm
                loading={loading}
                user={user}
                roles={roles}
                isPasswordVisible={isPasswordVisible}
                password={password}
                toggleShowPassword={this.toggleShowPassword}
                updatePassword={this.updatePassword}
                handleDeleteUser={this.handleDeleteUser}
                generatePassword={this.generatePassword}
              />
            </Form>
          </Validation>
        </Col>
      </Row>
    );
  }
}

AdminUserProfile.propTypes = {
  loading: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired
};

export default AdminUserProfile;
